/**
 * Cross-device and realtime sync for Message Requests (#3293, #3409). The PM
 * poll never reaches the Requests banner or list, so websocket frames and tab
 * refocus drive a server refetch here. Inert without the section URL.
 */
(function () {
  'use strict';

  // ThreadBox values as they ride the wire.
  var BOX_PRIMARY = 0;
  var BOX_REQUESTS = 1;

  var FOCUS_REFRESH_MS = 30000;

  var lastFocusRefresh = 0;
  var bannerInFlight = false;
  // A refetch asked for while one was already out, to run when it lands.
  var bannerAgain = false;
  // Threads unserved while a banner fetch was out. That fetch serves them stale, so its render drops them again.
  var unservedInFlight = [];
  // Set when an incoming request landed while the rendered list could not be
  // replaced, and consumed when that hold ends.
  var overlayStale = false;
  var COUNTER_REFRESH_MS = 400;
  // Longer: the sender drives edits and recalls, and each one would cost a refetch.
  var PREVIEW_REFRESH_MS = 1500;
  var counterRefreshTimer = null;
  // Last pmNewCnt seen, seeded from the nav badge the page rendered.
  var lastPmNewCnt = renderedPmNewCnt();

  function api() {
    return window.PmRequests || null;
  }

  function sectionUrl() {
    var main = document.getElementById('threads_main');
    return main ? (main.getAttribute('data-requests-section-url') || '') : '';
  }

  // Includes the declines already on the wire: the server answers a refresh
  // from its own state, which still counts them until the call lands.
  function pendingDeletes() {
    var undo = window.PmRequestsUndo;
    return (undo && typeof undo.heldCount === 'function') ? undo.heldCount() : 0;
  }

  /** Whether the rendered list is holding state a refresh would discard. */
  function listHeld() {
    var a = api();
    return !!(a && typeof a.listHeld === 'function' && a.listHeld());
  }

  // ───────────────────── banner ─────────────────────

  function bannerHost() {
    return document.getElementById('message-requests-host');
  }

  /**
   * Swap in a freshly rendered banner. The open trigger is reached by a
   * delegated listener, so replacing the node keeps it working, but the
   * keyboard and the icons live on the node itself and have to be put back.
   * Returns whether the banner was replaced. Only a replaced banner speaks for the server.
   */
  function renderBanner(html) {
    var host = bannerHost();
    if (!host) return false;

    // The server still counts a request whose decline is unsent, so an empty
    // section while a window is open cannot mean the queue is empty. Keeping
    // the banner leaves the undo somewhere to restore the count to.
    if (!(html || '').trim() && pendingDeletes() > 0) return false;

    // A lapsed session answers a fetch with the landing page, at status 200,
    // because fetch follows the redirect. Writing that into the pane would
    // bury the thread list under a whole second page. The prefix, not one id:
    // this route serves the banner or the setting-up notice.
    if ((html || '').trim() && (html || '').indexOf('id="message-requests-') === -1) return false;

    var active = document.activeElement;
    var hadFocus = !!(active && active.id === 'message-requests-open');
    var before = document.getElementById('message-requests-section');
    var unreadBefore = before ? before.getAttribute('data-unread-count') : null;
    var threadsBefore = before ? before.getAttribute('data-unread-threads') : null;

    host.innerHTML = html || '';
    var a = api();
    if (a && typeof a.bannerReplaced === 'function') a.bannerReplaced();
    if (a && typeof a.unserveThread === 'function') {
      unservedInFlight.forEach(function (id) { a.unserveThread(id, true); });
    }

    if (window.LucideIcons && typeof window.LucideIcons.initialize === 'function') {
      window.LucideIcons.initialize(host);
    }

    // The server counts a request whose delete is still inside its undo window,
    // so the rendered number is that many too high. Correcting it here, before
    // the browser paints, is what keeps the count from flicking back up.
    var section = document.getElementById('message-requests-section');
    if (section && a) {
      var served = parseInt(section.getAttribute('data-requests-count') || '0', 10);
      a.setCount(Math.max(0, served - pendingDeletes()));
    }

    // The rows carry unread styles. A reload would drop the scroll and the loaded pages, so restyle in place.
    var unreadAfter = section ? section.getAttribute('data-unread-count') : null;
    var threadsAfter = section ? section.getAttribute('data-unread-threads') : null;
    if (a && unreadBefore !== null && (unreadAfter !== unreadBefore || threadsAfter !== threadsBefore)) {
      a.markChanged();
    }
    // A queued fetch means this banner is already stale, and it could restyle a newly unread row.
    if (a && !bannerAgain) markReadRows(a, section);

    if (hadFocus) {
      var trigger = document.getElementById('message-requests-open');
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
    }
    return true;
  }

  /** Rows the banner confirms read. A row past the banner cap keeps its style until the next open refetches. */
  function markReadRows(a, section) {
    if (!section || !a.overlay() || typeof a.markServedReadRows !== 'function') return;
    a.markServedReadRows();
  }

  function refreshBanner() {
    var url = sectionUrl();
    if (!url) return;
    // A call already out was issued against older state, so its answer cannot
    // stand in for this one. Run again when it lands.
    if (bannerInFlight) { bannerAgain = true; return; }
    bannerInFlight = true;
    bannerAgain = false;
    unservedInFlight = [];
    var a = api();
    var mark = a && typeof a.localReadMark === 'function' ? a.localReadMark() : null;
    var done = function () {
      bannerInFlight = false;
      unservedInFlight = [];
      if (bannerAgain) refreshBanner();
      else replayServedReads();
    };
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        bannerInFlight = false;
        // The first banner after a load is the server's word on that read.
        if (renderBanner(html)) {
          if (mark !== null) a.settleLocalReads(mark);
          unservedInFlight = [];
          if (bannerAgain) refreshBanner();
          return;
        }
        done();
      })
      // Keep the banner as rendered. The next event or refocus retries.
      .catch(done);
  }

  /** The restyle applyLocalReads held back while a fetch was out. The banner kept in the DOM still stands. */
  function replayServedReads() {
    var a = api();
    if (a && typeof a.markServedReadRows === 'function') a.markServedReadRows();
  }

  // ───────────────────── overlay ─────────────────────

  function refreshOverlay() {
    var a = api();
    if (!a || !a.overlay()) return;
    if (listHeld()) {
      overlayStale = true;
      return;
    }
    overlayStale = false;
    a.reloadOverlay();
  }

  function refreshStaleOverlay() {
    if (!overlayStale || listHeld()) return;
    refreshOverlay();
  }

  // ───────────────────── events ─────────────────────

  /**
   * A request arrived, or came back to the queue. The banner and the overlay refetch
   * independently, so the thread's stale banner entry goes first: an overlay that
   * lands before the banner must not restyle the new unread row from it.
   */
  function onIncomingRequest(threadId) {
    if (!sectionUrl()) return;
    var a = api();
    if (a && threadId && typeof a.unserveThread === 'function') {
      a.unserveThread(threadId);
      if (bannerInFlight) unservedInFlight.push(String(threadId));
    }
    if (a) a.markChanged();
    refreshBanner();
    refreshOverlay();
  }

  function onThreadMoved(e) {
    var detail = e.detail;
    if (!detail || !detail.thread_id || !sectionUrl()) return;
    // A frame that names the same box on both sides moved nothing. Acting on a
    // redelivery would run the accept path a second time.
    if (detail.from_box !== null && detail.from_box === detail.to_box) return;

    var a = api();
    if (detail.to_box === BOX_PRIMARY) {
      var ownAccept = a ? a.applyMovedToPrimary(detail.thread_id) === true : false;
      if (!ownAccept) refreshBanner();
      return;
    }
    if (detail.to_box === BOX_REQUESTS) {
      // The open conversation goes back under the decision bar. Until the
      // request is accepted again there is no reply, and no read receipt.
      if (a && typeof a.applyMovedToRequests === 'function') {
        a.applyMovedToRequests(detail.thread_id);
      }
      onIncomingRequest(detail.thread_id);
    }
  }

  function onNewMessage(e) {
    var detail = e.detail;
    // An absent or unknown box is not a request. Only the split-active
    // recipient's own frames carry one.
    if (!detail || detail.box !== BOX_REQUESTS) return;
    onIncomingRequest(detail.thread_id);
  }

  function renderedPmNewCnt() {
    var badge = document.querySelector('[data-counter="pmNewCnt"][data-count]');
    var value = badge ? parseInt(badge.getAttribute('data-count'), 10) : NaN;
    return Number.isFinite(value) ? value : null;
  }

  function unreadShown() {
    var badge = document.getElementById('message-requests-unread-count');
    return !!(badge && !badge.classList.contains('hidden'));
  }

  /** One refetch for a burst of frames, such as a decline-all on another device. */
  function scheduleBannerRefresh(delay) {
    if (counterRefreshTimer) window.clearTimeout(counterRefreshTimer);
    counterRefreshTimer = window.setTimeout(function () {
      counterRefreshTimer = null;
      refreshBanner();
    }, delay || COUNTER_REFRESH_MS);
  }

  /**
   * A pmNewCnt drop can clear unread requests: a read, a block, a delete or a
   * decline. A rise is a new message, which thread:newmessage covers.
   */
  function onCounterUpdate(e) {
    var detail = e.detail;
    if (!detail || detail.id !== 'pmNewCnt') return;
    var value = parseInt(detail.value, 10);
    var previous = lastPmNewCnt;
    lastPmNewCnt = Number.isFinite(value) ? value : null;
    if (previous !== null && lastPmNewCnt !== null && lastPmNewCnt >= previous) return;
    // A local read hides the badge, but its hint still waits for a banner.
    var a = api();
    var localReads = !!(a && typeof a.hasLocalReads === 'function' && a.hasLocalReads());
    if (!sectionUrl() || (!unreadShown() && !localReads)) return;
    scheduleBannerRefresh();
  }

  /** A deleted thread sends no pmNewCnt frame when a decline-all syncs the counters in one push. */
  function onThreadDeleted() {
    if (!sectionUrl() || !unreadShown()) return;
    scheduleBannerRefresh();
  }

  /** An edit or a recall in the previewed request changes the preview text. */
  function onPreviewChanged(e) {
    var detail = e.detail;
    var preview = document.getElementById('message-requests-preview');
    if (!detail || !preview || !sectionUrl()) return;
    if (preview.getAttribute('data-thread-id') !== String(detail.thread_id)) return;
    // Only the previewed message changes the preview text.
    var shown = preview.getAttribute('data-message-id');
    if (detail.message_id && shown && shown !== String(detail.message_id)) return;
    scheduleBannerRefresh(PREVIEW_REFRESH_MS);
  }

  /**
   * Catch up after time away from the tab. The websocket can miss frames across
   * a sleep or a dropped connection, and this is the cheapest way back to the
   * server's view of the queue.
   */
  function onRefocus() {
    if (!sectionUrl()) return;
    var now = Date.now();
    if (now - lastFocusRefresh < FOCUS_REFRESH_MS) return;
    lastFocusRefresh = now;
    var a = api();
    if (a) a.markChanged();
    refreshBanner();
    // The rows are caught up too. A banner-only refresh would put the server's
    // count over a list that still shows what the tab was left with.
    refreshOverlay();
  }

  document.addEventListener('thread:moved', onThreadMoved);
  document.addEventListener('thread:newmessage', onNewMessage);
  document.addEventListener('ws:counter-update', onCounterUpdate);
  document.addEventListener('thread:deleted', onThreadDeleted);
  document.addEventListener('thread:edited', onPreviewChanged);
  document.addEventListener('thread:recalled', onPreviewChanged);
  document.addEventListener('pm:requests-select-changed', refreshStaleOverlay);
  // A reload whose response was dropped because the list was held. The page it
  // fetched is gone, so the list is behind the server until someone refetches.
  document.addEventListener('pm:requests-overlay-stale', function () {
    overlayStale = true;
  });

  /**
   * A closed window leaves the count on an optimistic local subtraction, and
   * the server is the only thing that knows what the decline actually did: a
   * batch may skip ids it no longer holds, and a 403 can mean either the
   * request was already gone or the split turned off underneath the page.
   */
  document.addEventListener('pm:requests-accept-settled', refreshBanner);
  document.addEventListener('pm:requests-read-settled', function () {
    if (sectionUrl()) scheduleBannerRefresh();
  });
  function onDeclinesSettled() {
    refreshBanner();
    refreshStaleOverlay();
  }
  document.addEventListener('pm:requests-undo-settled', onDeclinesSettled);
  // A decline-all holds no undo entry, so the refetch is the only count it trusts.
  document.addEventListener('pm:requests-decline-all-settled', onDeclinesSettled);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') onRefocus();
  });
  window.addEventListener('focus', onRefocus);

  window.PmRequestsLive = {
    /** Whether a banner fetch is out or queued, so the banner in the DOM may be stale. */
    bannerPending: function () { return bannerInFlight || bannerAgain; },
  };
})();
